/* lib/worker-url.ts — robust worker URL resolution.
 *
 * BEFORE: each view duplicated a 5-line readWorkerUrl() that read
 *   ?api=… → localStorage["forecast_worker_url"] → DEFAULT_WORKER_URL
 * without validation. A user with a stale `forecast_worker_url` (e.g.
 * pointing at the Pages domain instead of the Worker domain) would see
 * the UserPicker silently fail with "listUsers failed: 404 Not found"
 * because the Pages SPA fallback returned 200 with HTML for /api/users
 * — or worse, some out-of-band 404. Caught by Marco on 2026-05-16.
 *
 * AFTER: single shared resolver with three layers of defense:
 *   1. ?api=<url>  param  — must validate. Invalid → ignored.
 *   2. localStorage          — must validate. Invalid → REMOVED (self-heal).
 *   3. DEFAULT_WORKER_URL    — hardcoded, always valid.
 *
 * Validation rule: the URL must be a Cloudflare Worker hostname
 * (*.workers.dev) OR a localhost dev URL. This rejects accidental
 * pages.dev / custom-domain values that would route to the SPA
 * fallback and never return real JSON.
 *
 * Consolidates 5 duplicate definitions (UserPicker, viewer.tsx,
 * AdminAudit, AdminUsers, UserProfile). Future workers can be added
 * to the allowlist without touching any view.
 */

export const DEFAULT_WORKER_URL =
  "https://fonio-forecast-worker-v2.fonio-marco.workers.dev";

const STORAGE_KEY = "forecast_worker_url";

/** Allowlist regex. A URL passes if it matches at least one. */
const ALLOWED_PATTERNS: RegExp[] = [
  // Any Cloudflare Workers subdomain — covers both production workers
  // (fonio-forecast-worker, fonio-forecast-worker-v2) and any future
  // per-account *.workers.dev script we might deploy.
  /^https:\/\/[a-z0-9-]+(?:\.[a-z0-9-]+)*\.workers\.dev(?:\/.*)?$/i,
  // Wrangler local dev — `wrangler dev` defaults to 8787, but allow any
  // localhost port so Marco can override during debugging.
  /^http:\/\/localhost(?::\d+)?(?:\/.*)?$/i,
  /^http:\/\/127\.0\.0\.1(?::\d+)?(?:\/.*)?$/i,
];

function isValidWorkerUrl(url: unknown): url is string {
  if (typeof url !== "string" || !url) return false;
  // Strip trailing slash for matching consistency (allowed patterns
  // accept either form, but normalising helps log output).
  const u = url.trim().replace(/\/+$/, "");
  return ALLOWED_PATTERNS.some((re) => re.test(u));
}

/**
 * Resolve the worker URL with three layered fallbacks. Self-heals a
 * stale localStorage entry by deleting it when invalid — so the user
 * gets a working app on the next page load without needing to clear
 * storage manually.
 */
export function readWorkerUrl(): string {
  if (typeof window === "undefined") return DEFAULT_WORKER_URL;

  // 1. URL param override — useful for `?api=http://localhost:8787` during
  //    local dev. Must be valid; invalid values are ignored (no self-heal —
  //    the param will be gone on next navigation anyway).
  try {
    const q = new URLSearchParams(window.location.search).get("api");
    if (q && isValidWorkerUrl(q)) return q;
  } catch {
    /* unsafe environment — fall through */
  }

  // 2. localStorage — if present + valid, use it. If present + INVALID,
  //    remove it so we don't keep returning a broken URL.
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      if (isValidWorkerUrl(stored)) return stored;
      // Self-heal: remove the stale value and warn.
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
      // eslint-disable-next-line no-console
      console.warn(
        `[worker-url] Ignoring + removing invalid ${STORAGE_KEY} from localStorage: "${stored}". ` +
          `Expected a *.workers.dev or localhost URL. Falling back to default.`
      );
    }
  } catch {
    /* localStorage unavailable — fall through */
  }

  // 3. Hardcoded default — always valid.
  return DEFAULT_WORKER_URL;
}
